import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiFillHome } from "react-icons/ai";
import { FaMoneyCheckDollar } from "react-icons/fa6";
import { FcStatistics } from "react-icons/fc";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthActions, useAuthState } from "../../context/AppContext";
import { auth } from "../../firebase/firebaseConfig";
import { esAdminPrincipal } from "../../utils/adminAccess";
import type { NavbarLinkItem, NavbarMenuStatus, NavbarVisibility, UseNavbarReturn } from "./types";

const NAVBAR_ANIMATION_DURATION_MS = 500;
const HOME_NAV_ITEM: NavbarLinkItem = {
  path: "/home",
  label: "Inicio",
  mobileLabel: "Inicio",
  icon: AiFillHome,
};
const GASTOS_NAV_ITEM: NavbarLinkItem = {
  path: "/gastos-diarios",
  label: "Gastos Diarios",
  mobileLabel: "Gastos diarios",
  icon: FaMoneyCheckDollar,
};
const ESTADISTICAS_NAV_ITEM: NavbarLinkItem = {
  path: "/estadisticas",
  label: "Estadisticas",
  mobileLabel: "Estadisticas",
  icon: FcStatistics,
  iconClassName: "filter grayscale saturate-200",
};

export const obtenerVisibilidadNavbar = (
  userEmail: string | null | undefined,
  isAdmin: boolean | null | undefined,
): NavbarVisibility => {
  const isMainAdmin = esAdminPrincipal(userEmail);

  return {
    mostrarGastos: !(isMainAdmin || Boolean(isAdmin)),
    mostrarEstadisticas: !isMainAdmin,
  };
};

export const crearNavbarItems = (visibility: NavbarVisibility): NavbarLinkItem[] => {
  const items: NavbarLinkItem[] = [HOME_NAV_ITEM];

  if (visibility.mostrarGastos) {
    items.push(GASTOS_NAV_ITEM);
  }

  if (visibility.mostrarEstadisticas) {
    items.push(ESTADISTICAS_NAV_ITEM);
  }

  return items;
};

export const useNavbar = (): UseNavbarReturn => {
  const [menuStatus, setMenuStatus] = useState<NavbarMenuStatus>("closed");
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { handleLogout } = useAuthActions();
  const { isLogouting, userData } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const userEmail = auth.currentUser?.email;

  const visibility = useMemo(
    () => obtenerVisibilidadNavbar(userEmail, userData?.isAdmin),
    [userEmail, userData?.isAdmin],
  );

  const navItems = useMemo<NavbarLinkItem[]>(() => {
    return crearNavbarItems(visibility);
  }, [visibility]);

  const clearCloseTimeout = useCallback(() => {
    if (!closeTimeoutRef.current) {
      return;
    }

    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }, []);

  const handleGoHome = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const handleCloseNavbar = useCallback(() => {
    clearCloseTimeout();

    if (menuStatus === "closed" || menuStatus === "closing") {
      return;
    }

    setMenuStatus("closing");
    closeTimeoutRef.current = setTimeout(() => {
      setMenuStatus("closed");
      closeTimeoutRef.current = null;
    }, NAVBAR_ANIMATION_DURATION_MS);
  }, [clearCloseTimeout, menuStatus]);

  const handleToggleNavbar = useCallback(() => {
    if (menuStatus === "closed") {
      clearCloseTimeout();
      setMenuStatus("open");
      return;
    }

    handleCloseNavbar();
  }, [clearCloseTimeout, handleCloseNavbar, menuStatus]);

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const isMobileMenuOpen = menuStatus !== "closed";
  const mobileMenuAnimationClass =
    menuStatus === "closing" ? "animate__fadeOutLeft" : "animate__fadeInLeft";

  return {
    isMobileMenuOpen,
    mobileMenuAnimationClass,
    currentPath: location.pathname,
    isLogouting,
    navItems,
    handleGoHome,
    handleToggleNavbar,
    handleCloseNavbar,
    handleLogout,
  };
};
